import {
  getFromBff,
  getWithQuoteToken,
  getBlobWithQuoteToken,
  postToBffWithQuoteToken,
  postToBffWithRecaptcha,
  openQuoteSse,
  SseConexion,
  SseHandlers,
} from './autoBffClient';
import {
  BffCatalogoItem,
  BffContactoRequest,
  BffContactoResponse,
  BffCotizacionSnapshot,
  BffItemCatalogo,
  BffParametros,
  BffPasoResponse,
  BffPatenteResponse,
  BffPolizaResponse,
  BffProvincia,
  BffSeleccionResponse,
  BffSseDone,
  BffSseError,
  BffSseResult,
  BffSseStarted,
  BffValuacion,
  BffVersion,
  BffEmitirRequest,
  BffEstadoEmision,
  PasoEmision,
  Ramo,
  TipoDocumento,
} from './types';
import { exigirToken } from './quoteSession';

/**
 * Un wrapper por endpoint del contrato §3.1-§3.6. Devuelven el shape NUEVO tal
 * cual: la conversión al shape viejo la hacen los adaptadores (`adapters.ts`),
 * y el ruteo por flag lo hace la fachada (`index.ts`).
 */

const base = (ramo: Ramo) => `/${ramo}/v1`;

/* ───────────────── §3.1 bootstrap y catálogos ───────────────── */

export const getParametros = (
  ramo: Ramo,
  origen?: string
): Promise<BffParametros> =>
  getFromBff<BffParametros>(`${base(ramo)}/parametros`, { origen });

export const getMarcas = (
  ramo: Ramo,
  anio?: number
): Promise<BffItemCatalogo[]> =>
  getFromBff<BffItemCatalogo[]>(`${base(ramo)}/vehiculos/marcas`, { anio });

export const getModelos = (
  ramo: Ramo,
  marcaId: number
): Promise<BffItemCatalogo[]> =>
  getFromBff<BffItemCatalogo[]>(`${base(ramo)}/vehiculos/modelos`, { marcaId });

/**
 * Años del vehículo. `versionId` es opcional: el BFF devuelve los años de la
 * marca y el modelo asociados a esa versión, que es lo que necesita el paso de
 * datos cuando el vehículo vino del datacar.
 */
export const getAnios = (
  ramo: Ramo,
  marcaId: number,
  modeloId: number,
  versionId?: number
): Promise<number[]> =>
  getFromBff<number[]>(`${base(ramo)}/vehiculos/anios`, {
    marcaId,
    modeloId,
    versionId,
  });

export const getVersiones = (
  ramo: Ramo,
  marcaId: number,
  modeloId: number,
  anio: number
): Promise<BffVersion[]> =>
  getFromBff<BffVersion[]>(`${base(ramo)}/vehiculos/versiones`, {
    marcaId,
    modeloId,
    anio,
  });

export const getValuacion = (
  ramo: Ramo,
  versionId: number,
  anio: number
): Promise<BffValuacion> =>
  getFromBff<BffValuacion>(`${base(ramo)}/vehiculos/valuacion`, {
    versionId,
    anio,
  });

/** Solo auto: en moto el BFF responde 404 `not_applicable`. */
export const postPatente = (patente: string): Promise<BffPatenteResponse> =>
  postToBffWithRecaptcha<{ patente: string }, BffPatenteResponse>(
    '/auto/v1/vehiculos/patente',
    { patente },
    'patente'
  );

export const getProvincias = (ramo: Ramo): Promise<BffProvincia[]> =>
  getFromBff<BffProvincia[]>(`${base(ramo)}/lookups/provincias`);

export const getBancos = (ramo: Ramo): Promise<BffCatalogoItem[]> =>
  getFromBff<BffCatalogoItem[]>(`${base(ramo)}/catalogos/bancos`);

export const getTiposTarjeta = (
  ramo: Ramo,
  compania: string
): Promise<BffCatalogoItem[]> =>
  getFromBff<BffCatalogoItem[]>(`${base(ramo)}/catalogos/tipos-tarjeta`, {
    compania,
  });

export const getCondicionesIva = (
  ramo: Ramo,
  compania: string
): Promise<BffCatalogoItem[]> =>
  getFromBff<BffCatalogoItem[]>(`${base(ramo)}/catalogos/condiciones-iva`, {
    compania,
  });

export const getTiposInspeccion = (
  ramo: Ramo,
  compania: string,
  anio: number,
  esCeroKm: boolean
): Promise<BffCatalogoItem[]> =>
  getFromBff<BffCatalogoItem[]>(`${base(ramo)}/catalogos/tipos-inspeccion`, {
    compania,
    anio,
    esCeroKm,
  });

/* ───────────────── §3.2 contacto ───────────────── */

/** Emite el JWT de cotización. Recaptcha fresco, acción `contacto`. */
export const postContacto = (
  ramo: Ramo,
  body: BffContactoRequest
): Promise<BffContactoResponse> =>
  postToBffWithRecaptcha<BffContactoRequest, BffContactoResponse>(
    `${base(ramo)}/contacto`,
    body,
    'contacto'
  );

/* ───────────────── §3.3 precios ───────────────── */

export const abrirPreciosSse = (
  ramo: Ramo,
  companias: string[] | undefined,
  handlers: SseHandlers<BffSseStarted, BffSseResult, BffSseError, BffSseDone>
): SseConexion =>
  openQuoteSse<BffSseStarted, BffSseResult, BffSseError, BffSseDone>(
    `${base(ramo)}/precios`,
    companias && companias.length ? { companias } : {},
    exigirToken(),
    handlers
  );

/** Snapshot para reconexión o refresh de la pantalla de precios. */
export const getCotizacion = (
  ramo: Ramo,
  cotizacionId: string
): Promise<BffCotizacionSnapshot> =>
  getWithQuoteToken<BffCotizacionSnapshot>(
    `${base(ramo)}/cotizaciones/${encodeURIComponent(cotizacionId)}`,
    exigirToken()
  );

/* ───────────────── §3.4 selección y pasos ───────────────── */

export const postSeleccion = (
  ramo: Ramo,
  cotizacionId: string,
  itemId: string
): Promise<BffSeleccionResponse> =>
  postToBffWithQuoteToken<{ itemId: string }, BffSeleccionResponse>(
    `${base(ramo)}/cotizaciones/${encodeURIComponent(cotizacionId)}/seleccion`,
    { itemId },
    exigirToken()
  );

/**
 * Tracking de abandono por paso. NUNCA lleva datos de pago: el BFF responde
 * 400 `payment_data_not_allowed` si los ve.
 */
export const postPaso = (
  ramo: Ramo,
  cotizacionId: string,
  paso: PasoEmision,
  datos: Record<string, unknown>
): Promise<BffPasoResponse> =>
  postToBffWithQuoteToken<
    { paso: PasoEmision; datos: Record<string, unknown> },
    BffPasoResponse
  >(
    `${base(ramo)}/cotizaciones/${encodeURIComponent(cotizacionId)}/pasos`,
    { paso, datos },
    exigirToken()
  );

/* ───────────────── §3.5 emisión ───────────────── */

/**
 * `Idempotency-Key` la genera el front UNA vez por intento de emisión y la
 * reusa en los reintentos: es lo que permite que un 504 o un 409
 * `issue_in_progress` no termine en una póliza duplicada.
 */
export const postEmitir = (
  ramo: Ramo,
  cotizacionId: string,
  body: BffEmitirRequest,
  idempotencyKey: string
): Promise<BffPolizaResponse> =>
  postToBffWithQuoteToken<BffEmitirRequest, BffPolizaResponse>(
    `${base(ramo)}/cotizaciones/${encodeURIComponent(cotizacionId)}/emitir`,
    body,
    exigirToken(),
    { 'Idempotency-Key': idempotencyKey }
  );

/* ───────────────── §3.6 documentos ───────────────── */

/**
 * Estado de la emisión de una cotización. Es lo que permite resolver un 409
 * `issue_in_progress` o un 504 `provider_timeout` por polling, sin pedirle a
 * la persona que vuelva a apretar Contratar.
 */
export const getEstadoEmision = (
  ramo: Ramo,
  cotizacionId: string
): Promise<BffEstadoEmision> =>
  getWithQuoteToken<BffEstadoEmision>(
    `${base(ramo)}/cotizaciones/${encodeURIComponent(cotizacionId)}/emision`,
    exigirToken()
  );

export const getPoliza = (
  ramo: Ramo,
  polizaId: string
): Promise<BffPolizaResponse> =>
  getWithQuoteToken<BffPolizaResponse>(
    `${base(ramo)}/polizas/${encodeURIComponent(polizaId)}`,
    exigirToken()
  );

export const getDocumento = (
  ramo: Ramo,
  polizaId: string,
  kind: TipoDocumento
): Promise<Blob> =>
  getBlobWithQuoteToken(
    `${base(ramo)}/polizas/${encodeURIComponent(polizaId)}/documentos/${kind}`,
    exigirToken()
  );

export const postCertificadoNoRodamiento = (
  ramo: Ramo,
  polizaId: string,
  archivo: { nombre: string; extension: string; dataBase64: string }
): Promise<void> =>
  postToBffWithQuoteToken<typeof archivo, void>(
    `${base(ramo)}/polizas/${encodeURIComponent(
      polizaId
    )}/certificado-no-rodamiento`,
    archivo,
    exigirToken()
  );
